interface HeroProps {
  title: string;
  description: string;
  imageSrc: string;
  overlay?: boolean;
  fullHeight?: boolean;
  ctaLabel?: string;
  ctaHref?: string;
  eyebrow?: string;
}

export const Hero = ({
  title,
  description,
  imageSrc,
  overlay = true,
  fullHeight = false,
  ctaLabel,
  ctaHref,
  eyebrow,
}: HeroProps) => {
  return (
    <div
      className={`relative w-full overflow-hidden ${
        fullHeight
          ? 'min-h-[clamp(560px,82vh,760px)]'
          : 'min-h-[clamp(380px,55vh,560px)]'
      }`}
    >
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src={imageSrc}
          alt={title}
          className="object-cover w-full h-full scale-[1.02]"
        />
        {/* Directional gradient (not a flat dark wash) */}
        <div
          className="absolute inset-0"
          style={{
            background: overlay
              ? 'linear-gradient(115deg, rgba(13,10,0,0.78) 0%, rgba(13,10,0,0.5) 45%, rgba(13,10,0,0.15) 100%)'
              : 'linear-gradient(115deg, rgba(13,10,0,0.72) 0%, rgba(13,10,0,0.35) 50%, rgba(13,10,0,0.05) 100%)',
          }}
        />
        {/* Subtle film grain via tinted noise — fixed, non-scrolling cost */}
        <div
          className="absolute inset-0 mix-blend-overlay opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.5'/></svg>\")",
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col justify-end pb-16 md:pb-24 min-h-[inherit]">
        <div className="container mx-auto px-6 sm:px-10 md:px-16">
          <div className="max-w-[820px]">
            <p className="eyebrow text-white/70 mb-6 animate-fade-in">
              {eyebrow ?? 'Sportcommunity · Ten Boer & Bedum'}
            </p>
            <h1 className="text-white text-[clamp(40px,7vw,82px)] font-medium leading-[0.98] tracking-[-0.035em] mb-7 animate-fade-in-up">
              {title.split(' ').slice(0, -1).join(' ')}{' '}
              <span className="font-serif-display italic font-normal text-[#F5B800]">
                {title.split(' ').slice(-1)}
              </span>
            </h1>
            <p
              className="text-white/75 text-[17px] md:text-[19px] max-w-[560px] leading-[1.55] animate-fade-in-up"
              style={{ animationDelay: '0.1s' }}
            >
              {description}
            </p>
            {ctaLabel && ctaHref && (
              <div className="mt-10 flex items-center gap-3 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                <a
                  href={ctaHref}
                  className="btn-pill"
                  style={{ background: '#F5B800', color: '#0D0A00' }}
                >
                  {ctaLabel}
                  <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6H10M10 6L6 2M10 6L6 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </a>
                <a
                  href="/contact"
                  className="btn-pill"
                  style={{ background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}
                >
                  Neem contact op
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom border seam — subtle editorial detail */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-white/10 z-10" />
    </div>
  );
};
